<template>
  <div>
    <div class="d-flex align-center">
      <h2>Class Diary</h2>
      <v-spacer />
      <v-btn
        @click="downloadDiaries"
        :disabled="isSearching"
        color="primary"
        variant="outlined"
      >
        <v-icon start>mdi-tray-arrow-down</v-icon>
        {{ $t('common.download') }}
      </v-btn>
    </div>

    <v-card variant="flat" class="mt-6 pa-2" color="grey-lighten-5">
      <v-container fluid>
        <v-row align="center">
          <v-col cols="3">
            <h5>Class</h5>
            <v-select
              v-model="classId"
              :items="classes"
              :item-title="(item) => `(${item.year}) ${item.className}`"
              item-value="classId"
              variant="outlined"
              hide-details
              density="compact"
              clearable
              :disabled="isSearching"
              color="primary"
            ></v-select>
          </v-col>
          <v-col cols="3">
            <h5>Date (Start)</h5>
            <date-picker v-model="from"></date-picker>
          </v-col>
          <v-col cols="3">
            <h5>Date (End)</h5>
            <date-picker v-model="to"></date-picker>
          </v-col>
          <h5
          class="mt-4 pl-4"
          >
          Unapproved
          </h5>
          <v-col
            cols="1"
            class="mt-10 ml-0 pl-0"
            >
            <v-checkbox
              v-model="check"
              :disabled="isSearching"
              color="primary"
            >
            </v-checkbox>
          </v-col>
          <v-col cols="1">
            <v-btn
              class="mt-4 search-button"
              color="primary"
              variant="flat"
              large
              @click="searchBtn"
              :loading="isSearching"
            >
              {{ $t('common.search') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <div>
      <div class="d-flex mt-4">
        <h3>Search Results: {{ diaries.length }} items</h3>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="flat"
          large
          :to="`/class-diaries/create?classId=${classId}`"
        >
          <v-icon start>mdi-plus</v-icon>
          {{ $t('common.create') }}
        </v-btn>
      </div>

      <div class="my-4">
        <v-card
          v-for="d in sorted"
          :key="d.classDiaryId"
          class="py-2 px-4 my-2"
          variant="outlined"
        >
          <div class="d-flex align-center">
            <div class="d-flex">
              <div class="font-weight-bold mr-4">
                {{ getClassName(d.classId) }}
              </div>
              <div>{{ formatDate(d.date) }}</div>
            </div>
            <span
              v-if="isToday(d.date)"
              class="font-weight-bold primary--text ml-2"
              >Today's Diary</span
            >
            <v-spacer></v-spacer>
            <div class="d-flex">
              <v-chip
                v-if="d.approvedBy.chief && d.approvedBy.chief != -1"
                color="primary"
                class="mx-1"
                label
              >
                <v-icon start>mdi-check</v-icon>
                Chief
              </v-chip>
              <v-chip
                v-if="d.approvedBy.director && d.approvedBy.director != -1"
                color="primary"
                class="mx-1"
                label
              >
                <v-icon start>mdi-check</v-icon>
                Director
              </v-chip>
              <div v-if="d.status == 'draft'" class="d-flex">
                <v-chip variant="outlined" class="mx-1">Draft </v-chip>
                <v-btn
                  @click="deleteClassDiary(d.classDiaryId)"
                  color="red"
                  class="mx-1"
                  variant="flat"
                  dark
                >
                  {{ $t('common.delete') }}
                </v-btn>
              </div>
              <v-chip
                v-if="d.status === 'applied'"
                color="primary"
                variant="outlined"
                class="mx-1"
                >Applied</v-chip
              >
              <!-- <v-chip
                v-if="d.approvedBy.director && d.approvedBy.director != -1"
                color="primary"
              >承認済み</v-chip> -->
              <!-- <v-chip
                v-else-if="d.status === 'draft'"
                variant="outlined"
              >下書き</v-chip>
              <v-chip
                v-else-if="d.status === 'applied'"
                color="primary"
                variant="outlined"
              >申請済み</v-chip> -->
            </div>
            <v-btn
              class="ml-4"
              variant="flat"
              color="primary"
              :to="`/class-diaries/${d.classId}/${d.classDiaryId}`"
            >
              {{ $t('common.edit') }}
            </v-btn>
          </div>
        </v-card>
      </div>
    </div>

    <v-pagination
      class="pt-4"
      v-model="page"
      @input="pageClick"
      :length="Math.ceil(totalItemsCount / limit)"
    >
    </v-pagination>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";

import DatePicker from "@/components/atoms/DatePicker.vue";
import { classDiaryModle } from "@/store/dataModules/classDiaryModue";
import { classModule } from "@/store/dataModules/classModule";

import { ClassDiaryResponse, NurseryClassResponse } from "chaild-api/lib";

import LocalDataService from "@/service/LocalDataService";
import ApiClassDiary from "@/api/ApiClassDiary";

interface DataType {
  isSearching: boolean;
  page: number;
}

export default defineComponent({
  components: {
    DatePicker,
  },
  computed: {
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    diaries(): ClassDiaryResponse[] {
      return classDiaryModle.diaries;
    },
    sorted(): ClassDiaryResponse[] {
      return [...this.diaries].sort((d1, d2) => {
        const date1 = dayjs(d1.date);
        const date2 = dayjs(d2.date);
        return date2.diff(date1, "day");
      });
    },
    classId: {
      get() {
        return classDiaryModle.classId;
      },
      set(value: number | null) {
        classDiaryModle.setClassId(value);
      },
    },
    from: {
      get() {
        return classDiaryModle.from;
      },
      set(value: string) {
        classDiaryModle.setFrom(value);
      },
    },
    to: {
      get() {
        return classDiaryModle.to;
      },
      set(value: string) {
        classDiaryModle.setTo(value);
      },
    },
    check: {
      get() {
        return classDiaryModle.check;
      },
      set(value: boolean){
        classDiaryModle.setCheck(value);
      },
    },
    limit: function (): number {
      return classDiaryModle.limit;
    },
    totalItemsCount: function (): number {
      return classDiaryModle.totalItemsCount;
    },
  },

  data: (): DataType => {
    return {
      isSearching: false,
      page: 1,
    };
  },

  watch: {
    page: {
      handler(val) {
        classDiaryModle.setSkip(val - 1);
        this.search();
      },
    },
  },

  methods: {
    async searchBtn() {
      this.page = 1;
      sessionStorage.setItem('classDiariesPage', String(1));
      await this.search();
    },
    async search() {
      this.isSearching = true;
      if (this.classId) sessionStorage.setItem('classDiariesClassId', String(this.classId)); else sessionStorage.setItem('classDiariesClassId', '');
      if (this.from) sessionStorage.setItem('classDiariesFrom', this.from); else sessionStorage.setItem('classDiariesFrom', '');
      if (this.to) sessionStorage.setItem('classDiariesTo', this.to); else sessionStorage.setItem('classDiariesTo', '');
      if (this.check) sessionStorage.setItem('classDiariesCheck', "true"); else sessionStorage.setItem('classDiariesCheck', "false");
      await classDiaryModle.listDiary().catch(() => (this.isSearching = false));
      this.isSearching = false;
    },
    async pageClick(pageNumber) {
      if (pageNumber) sessionStorage.setItem('classDiariesPage', String(pageNumber));
    },
    async deleteClassDiary(classDiaryId: string) {
      if (confirm("Are you sure you want to delete?")) {
        this.isSearching = true;
        const response = await classDiaryModle.deleteClassDiary({
          classDiaryId,
        });
        if (response) {
          this.search();
        }
        this.isSearching = false;
      }
    },
    isToday(date: string) {
      return dayjs().format("YYYY-MM-DD") === date;
    },
    formatDate(date: string): string {
      return date ? dayjs(date).format("DD/MM/YYYY") : "";
    },
    getClassName(classId: number) {
      const find = this.classes.find((c) => c.classId === classId);
      if (find) {
        return find.className;
      }
      return "Unknown class";
    },
    async downloadDiaries() {
      this.isSearching = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        // request DL
        const classIds: number[] = [];
        if (classDiaryModle.classId) {
          classIds.push(classDiaryModle.classId);
        }
        const diaryResponse = await ApiClassDiary.downloadClassDiaries({
          nurseryId,
          classIds: classIds,
          from: classDiaryModle.from || undefined,
          to: classDiaryModle.to || undefined,
          check: classDiaryModle.check,
        }).catch(() => (this.isSearching = false));
        if (diaryResponse) {
          alert(
            "Generating download file.\nOnce complete, you can download it from the Download List page."
          );
          this.$router.push({ path: "/download/list" });
        }
      }
    },
  },

  async mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      await classModule.listClass({
        nurseryId: nurseryId,
      });
      const classDiariesClassId = sessionStorage.getItem('classDiariesClassId');
      const classDiariesFrom = sessionStorage.getItem('classDiariesFrom');
      const classDiariesTo = sessionStorage.getItem('classDiariesTo');
      const classDiariesCheck = sessionStorage.getItem('classDiariesCheck');
      const classDiariesPage = sessionStorage.getItem('classDiariesPage');

      if (classDiariesClassId) this.classId = Number(classDiariesClassId);
      if (classDiariesFrom) this.from = classDiariesFrom;
      if (classDiariesTo) this.to = classDiariesTo;
      if (classDiariesCheck == "true") this.check = true; else this.check = false;

      await this.search();
      if (classDiariesPage) this.page = Number(classDiariesPage);
    }
  },
});
</script>
